import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Delivery, Item } from "@/services/backend/samples/types/delivery";
import { ReactNode, useState } from "react";
import {
  useSamplesDeliveryPutMutation,
  useSamplesDeliverySendMutation,
} from "@/services/backend/samples/api/delivery-api";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  ScrollBodyTable,
  ScrollBodyTableHeader,
} from "@/shared/components/tables/table-scroll-body";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/shared/components/ui/table";
import { useSamplesViewModelShowQuery } from "@/services/backend/samples/api/sample-api";
import { AlertTriangle, X } from "lucide-react";
import { Field, FieldError, FieldLabel } from "@/shared/components/ui/field";
import { LaboratorySelect } from "@/services/backend/samples/components/laboratory-select";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { toast } from "sonner";
import { fieldErrors } from "@/shared/components/errors/field-errors";
import { Separator } from "@/shared/components/ui/separator";

export function EditDeliveryDialog({
  delivery,
  trigger,
}: {
  delivery: Delivery;
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(delivery);
  const [put, { isLoading, error, reset }] = useSamplesDeliveryPutMutation();

  const [
    send,
    { isLoading: sendIsLoading, error: sendError, reset: sendReset },
  ] = useSamplesDeliverySendMutation();

  const doUpdate = () => {
    if (isLoading) return;
    put(request)
      .unwrap()
      .then(() => {
        toast.success("Lieferung aktualisiert");
        resetState();
        setOpen(false);
      });
  };

  const doSend = () => {
    if (sendIsLoading) return;
    send({ id: delivery.id })
      .unwrap()
      .then(() => {
        toast.success("Lieferung bei Labor angemeldet");
        resetState();
        setOpen(false);
      });
  };

  const resetState = () => {
    setRequest(delivery);
    reset();
    sendReset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        resetState();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        {trigger || <Button>{delivery.trackingId}</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t("Lieferung")}</DialogTitle>
          <DialogDescription>
            {t("Hier können Sie die Lieferung überarbeiten und absenden.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel>{t("Labor")}</FieldLabel>
            <LaboratorySelect
              labId={request.labId}
              onLabIdChange={(labId) => setRequest({ ...request, labId })}
            />
            <FieldError errors={fieldErrors(error || sendError, "labId")} />
          </Field>
          <Field>
            <FieldLabel>{t("Tracking ID")}</FieldLabel>
            <Input
              value={request.trackingId}
              onChange={(e) =>
                setRequest({ ...request, trackingId: e.target.value })
              }
            />
            <FieldError
              errors={fieldErrors(error || sendError, "trackingId")}
            />
          </Field>
          <Field className="col-span-2">
            <FieldLabel>{t("Beschreibung (Notiz an das Labor)")}</FieldLabel>
            <Textarea
              value={request.description}
              onChange={(e) =>
                setRequest({ ...request, description: e.target.value })
              }
            />
            <FieldError
              errors={fieldErrors(error || sendError, "description")}
            />
          </Field>
          <div className="col-span-2">
            <ScrollArea className={error ? "h-[20vh]" : "h-[40vh]"}>
              <ScrollBodyTable>
                <ScrollBodyTableHeader>
                  <TableRow>
                    <TableHead>{t("Nummer")}</TableHead>
                    <TableHead>{t("Art")}</TableHead>
                    <TableHead>{t("Ort")}</TableHead>
                    <TableHead>{t("Stelle")}</TableHead>
                    <TableHead />
                    <TableHead />
                  </TableRow>
                </ScrollBodyTableHeader>
                <TableBody>
                  {request.items.map((item) => (
                    <DeliveryItemRow
                      key={item.id}
                      item={item}
                      onRemove={(it) =>
                        setRequest({
                          ...request,
                          items: request.items.filter((i) => i.id !== it.id),
                        })
                      }
                      error={fieldErrors(
                        error || sendError,
                        `items.${item.id}`,
                      )}
                    />
                  ))}
                </TableBody>
              </ScrollBodyTable>
            </ScrollArea>
          </div>
        </div>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doUpdate} disabled={isLoading || sendIsLoading}>
            {t("Aktualisieren")}
          </Button>
          <Separator orientation="vertical" />
          <Button onClick={doSend} disabled={isLoading || sendIsLoading}>
            {t("An Labor Senden")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryItemRow({
  item,
  onRemove,
  error,
}: {
  item: Item;
  onRemove: (r: Item) => void;
  error: Array<{ message?: string } | undefined>;
}) {
  const { data: sample } = useSamplesViewModelShowQuery({ id: item.sampleId });

  if (!sample) {
    return null;
  }

  return (
    <TableRow>
      <TableCell>{sample.number}</TableCell>
      <TableCell>{t(sample.kind)}</TableCell>
      <TableCell>{sample.location}</TableCell>
      <TableCell>{sample.spot}</TableCell>
      <TableCell>
        {error.map((e) => (
          <span
            key={e?.message}
            className="text-destructive flex items-center gap-2"
          >
            <AlertTriangle className="size-5" />
            {e?.message}
          </span>
        ))}
      </TableCell>
      <TableCell className="text-right">
        <Button size="sm" variant="destructive" onClick={() => onRemove(item)}>
          <X />
        </Button>
      </TableCell>
    </TableRow>
  );
}
